<template>
  <div
    class="relative"
    :class="{ 'overflow-y-scroll scrollbar-hide': mode === 'vertical' }"
  >
    <slot name="header" />
    <div
      ref="sliderRef"
      class="flex size-full shrink-0 snap-mandatory scrollbar-hide"
      :class="[
        {
          container,
          'space-x-4': spacedItems,
          'snap-y flex-col overflow-x-hidden overflow-y-scroll':
            mode === 'vertical',
          'snap-x overflow-x-auto overflow-y-hidden': mode === 'horizontal',
        },
        sliderClass,
      ]"
      :tabindex="sliderTabindex ?? 0"
      role="region"
      :aria-label="
        mode === 'horizontal' ? 'Image carousel' : 'Image thumbnails'
      "
      @scroll.passive="onScroll"
    >
      <slot />
    </div>
    <component :is="divOrTransition" :duration="150">
      <div v-if="withArrows">
        <slot
          name="arrows"
          v-bind="{
            prev,
            isPrevEnabled,
            canGoPrev,
            next,
            isNextEnabled,
            canGoNext,
            isScrollable,
          }"
        >
          <slot name="prev-button" v-bind="{ prev, canGoPrev }">
            <button
              v-if="canGoNext"
              class="absolute rounded-full text-gray-400 hover:text-orange-500 disabled:opacity-10"
              :class="{
                'disabled:hidden': hideDisabledArrows,
                'left-2 top-[40%]': mode === 'horizontal',
                'left-1/2 top-2 -translate-x-1/2 rotate-90':
                  mode === 'vertical',
              }"
              aria-label="prev button"
              @click="prev()"
            >
              <IconChevronLeft class="size-9" />
            </button>
          </slot>
          <slot name="next-button" v-bind="{ next, canGoNext }">
            <button
              v-if="canGoNext"
              class="absolute rounded-full text-gray-400 hover:text-orange-500 disabled:opacity-10"
              :class="{
                'disabled:hidden': hideDisabledArrows,
                'right-2 top-[40%]': mode === 'horizontal',
                'bottom-2 left-1/2 -translate-x-1/2 rotate-90':
                  mode === 'vertical',
              }"
              aria-label="next button"
              @click="next()"
            >
              <IconChevronRight class="size-9" />
            </button>
          </slot>
        </slot>
      </div>
    </component>
    <slot
      name="thumbnails"
      :scroll-image-into-view="scrollImageIntoView"
      :active-slide="activeSlide"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, type Ref, watch } from 'vue'
import { useMounted, onKeyStroke } from '@vueuse/core'
import { useItemsSlider } from '#storefront-ui'
import { SFFadeInTransition } from '#storefront-ui/components'

const {
  container = false,
  spacedItems = false,
  withArrows = false,
  sliderClass = '',
  hideDisabledArrows = false,
  loop = false,
  mode = 'horizontal',
} = defineProps<{
  container?: boolean
  sliderClass?: string
  spacedItems?: boolean
  withArrows?: boolean
  hideDisabledArrows?: boolean
  loop?: boolean
  mode?: 'horizontal' | 'vertical'
  sliderTabindex?: number
}>()

const sliderRef = ref<HTMLElement>()

const isMounted = useMounted()

const {
  next,
  prev,
  isNextEnabled,
  isPrevEnabled,
  canGoNext,
  canGoPrev,
  onScroll,
  scrollImageIntoView,
  activeSlide,
  isScrollable,
} = useItemsSlider(sliderRef as Ref<HTMLElement>, mode, loop)

const divOrTransition = computed(() => {
  return !isMounted.value ? 'div' : SFFadeInTransition
})

nextTick(() => onScroll())

defineExpose({
  scrollImageIntoView,
})

const emit = defineEmits<{
  'update:activeSlide': [newActiveSlide: number]
}>()
watch(activeSlide, (newActiveSlide) => {
  emit('update:activeSlide', newActiveSlide)
})

// Handle keyboard navigation for accessibility
onKeyStroke(
  ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'],
  (event: KeyboardEvent) => {
    event.preventDefault()

    if (mode === 'horizontal') {
      if (event.key === 'ArrowLeft' && canGoPrev.value) {
        prev()
      } else if (event.key === 'ArrowRight' && canGoNext.value) {
        next()
      }
    } else {
      if (event.key === 'ArrowUp' && canGoPrev.value) {
        prev()
      } else if (event.key === 'ArrowDown' && canGoNext.value) {
        next()
      }
    }
  },
  { target: sliderRef },
)
</script>
